//////////////////////////////////////////////////////////////////////////////
// 3D Trajectory Demo: demonstrates the use of the GIS Object to display
// 3D trajectories with ground tracks and drop lines.
// The GLG Map Server is used to display a globe in the orthographic 
// projection.
//
// The demo is written in pure HTML5 and JavaScript. The source code of the
// demo uses the GLG Toolkit JavaScript Library supplied by the included
// Glg*.js and GlgToolkit*.js files. The GLG library loads a GLG drawing
// and renders it on a web page, providing an API to handle user interaction
// with graphical objects in the drawing.
//
// The drawings are created using the GLG Graphics Builder, an interactive
// editor that allows to create grahical objects and define their dynamic
// behavior without any programming. The airplane icons are dynamic
// GLG objects created with the Graphics Builder.
//
// The background map is generated by the GLG Map Server, which is integrated
// inside of a GLG drawing as a GLG GIS Object. The GIS Object takes a
// complete care of the map display, automatically generating Map Server
// requests to re-generate the image when the map is panned or zoomed.
//
// By default, demo uses an asyncronous map loading request: when the globe is
// rotated, the program will continue to operate with the current globe view
// and will switch to the new globe view when the new map is ready.
// Alternatively, the USE_REQUEST variable in the code below may be set to false
// to change the orbit to match the new globe view right away when the globe
// is rotated, but no map will be displayed while the new map is being loaded.
//
// Except for the changes to comply with the JavaScript syntax, this source
// is identical to the source code of the corresponding C/C++/C# and Java
// desktop versions of the demo.
//
// This demo uses the GLG Map Server to display a map.
// The Map Server has to be installed either on the local host or on a
// remote web server. After the Map Server has been installed, modify the
// source code to set the SuppliedMapServerURL variable to point to the
// Map Server location.
//////////////////////////////////////////////////////////////////////////////

/* eslint eqeqeq: 0 */

import { GlgToolkit } from '../GlgToolkitDemo.mod.js'

// Enable general debugging/diagnostics information.
const DEBUG = false;

/* Debugging: set the variable to true to throw an exception on a GLG error
   instead of just displaying an error message on the console.
*/
const DEBUG_GLG_ERRORS = false;

/* This demo uses the GLG Map Server to display a map.
   The Map Server has to be installed either on the local host or on a
   remote web server. After the Map Server has been installed, set the 
   SuppliedMapServerURL variable to point to the Map Server location:

   For example on Linux:
     SuppliedMapServerURL = "http://localhost/cgi-bin/GlmScript";
   or
     SuppliedMapServerURL = "http://www.myserver.com/cgi-bin/GlmScript";

   On Windows:
     SuppliedMapServerURL = "http://localhost/Scripts/GlmScript.pl";
   or
     SuppliedMapServerURL = "http://www.myserver.com/Scripts/GlmScript.pl";

   The SuppliedMapServerURL value overrides the URL of the GIS object in the
   drawing.
*/
const SuppliedMapServerURL = null;    /* String */

/* Set to true to use asynchronous map requests to load the new map: when the
   globe is rotated, the program will continue to operate with the current 
   globe view and will switch to the new globe view when the new map is ready.         
   If set to false, the program will change the orbit to match the new globe 
   view right away when the globe is rotated, but no map will be displayed 
   while the new map is being loaded.

   To preserve the 3D viewing persperctive, the ZoomIn and ZoomOut functionality
   in this demo is implemented via GIS object scaling instead of the GIS Zooming.
   As the result, the ZoomIn and ZoomOut operations are not affected by the 
   USE_REQUEST setting.
*/
const USE_REQUEST = true;

const UPDATE_INTERVAL = 30;   // Update interval in msec

const NUM_ITERATIONS = 500;   // Number of iterations along the trajectory.

// Display a drop line for every DROP_LINE_INTERVAL iterations.
const DROP_LINE_INTERVAL = 20;    

// Global handle to the GLG Toolkit library.
let GLG = new GlgToolkit();

//////////////////////////////////////////////////////////////////////////////
// Creates an instance of the trajectory demo.
// Parameters:
//   glg_div_name  - name of parent div the drawing will be displayed in,
//                   will be passed by the caller.
//   is_mobile     - true if deployed on mobile devices.
//   is_standalone - true if deployed in html, false if deployed in react or
//                   angular.
//   glg_path      - path to the directory where GLG drawings are located.
//////////////////////////////////////////////////////////////////////////////
export function GlgTrajectory( glg_div_name, glg_path, is_standalone, is_mobile )
{
   Debug( "New script for " + glg_div_name + ": "  +
          Object.getPrototypeOf( this ).constructor.name +
          " glg_path: " + glg_path );
    
   if( DEBUG_GLG_ERRORS )   
     GLG.ThrowExceptionOnError( true, true, true );
   else
     GLG.ThrowExceptionOnError( false, false, false );

   this.GLG_div_name = glg_div_name;
   this.IsMobile = is_mobile;
   this.Standalone = is_standalone;    

   // Use path to the drawings directory is supplied.
   this.GlgPath = glg_path;

   // GlgObject: top level viewport of the loaded drawing.
   this.Drawing = null;                  /* GlgObject */

   this.CraftIcon = new Array( 2 );      /* GlgObject[2] */
   this.Map = null;                      /* GlgObject : map viewport */
   this.GISObject = null;                /* GlgObject */
   this.GISArray = null;                 /* GlgObject */
   this.TrajectoryEdgeTemplate = null;   /* GlgObject */
   this.TrajectoryFillTemplate = null;   /* GlgObject */
   this.DropLineTemplate = null;         /* GlgObject */
   this.Craft = null;                    /* GlgObject */
   this.TrajectoryEdge = null;           /* GlgObject */
   this.TrajectoryFill = null;           /* GlgObject */
   this.DropLineArray = null;            /* GlgObject */
   /* Provides visual feedback when the user defines the trajectory's start
      and end points with the mouse.
   */
   this.NewTrajectoryPolygon = null;     /* GlgObject */

   // Stores the center of the GIS projection.
   this.GISCenter = null;                                /* GlgPoint */

   // The last craft position, is used to calculate heading angles.
   this.PrevPosition = GLG.CreateGlgPoint( 0, 0, 0 );    /* GlgPoint */ 
   this.Position = GLG.CreateGlgPoint( 0, 0, 0 );        /* GlgPoint */ 
   this.GroundPosition = GLG.CreateGlgPoint( 0, 0, 0 );  /* GlgPoint */ 
   this.CursorPosition = GLG.CreateGlgPoint( 0, 0, 0 );  /* GlgPoint */ 

   this.UpdateCount = 0;                 /* int */
   this.ZoomLevel = 2;                   /* int */

   this.Pause = false;                   /* boolean */
   this.NewTrajectoryMode = false;       /* boolean */

   // Simulation parameters.

   // Maximum height of the trajectory, may be changed with a slider.
   this.TrajectoryHeight = 400000.0;     /* double */

   // Trajectory curvature value, may be changed with a slider.
   this.Curvature = -1.75;               /* double */
   this.CurvatureY = null;               /* double */

   /* Trajectory length in abstract units used for the simulation. */
   this.TrajectoryLength = null; /* double */
   
   /* Initial GlgPoint values for the start and end of the trajectory; 
      may be changed interactively using the mouse. */
   this.StartPoint =
     GLG.CreateGlgPoint( -80.644861,       28.572872,       0.0 );
   this.EndPoint =
     GLG.CreateGlgPoint( -80.644861 + 5.0, 28.572872 - 3.0, 0.0 );

   /* Variables used to define the trajectory's start and end points with 
      the mouse. */
   this.Point1 = GLG.CreateGlgPoint( 0, 0, 0 );            /* GlgPoint */
   this.Point2 = GLG.CreateGlgPoint( 0, 0, 0 );            /* GlgPoint */

   // Temporary objects used for simulation math.
   this.CraftAngles = GLG.CreateGlgPoint( 0, 0, 0 );       /* GlgPoint */
   this.CurrXYZ = GLG.CreateGlgPoint( 0, 0, 0 );           /* GlgPoint */
   this.PrevXYZ = GLG.CreateGlgPoint( 0, 0, 0 );           /* GlgPoint */
   this.TrajectoryVector = GLG.CreateGlgPoint( 0, 0, 0 );  /* GlgPoint */

   // Normal to the trajectory's general direction.
   this.NormalVector = GLG.CreateGlgPoint( 0, 0, 0 );      /* GlgPoint */

   this.MapLoaded = false;                /* boolean */
   this.InitialLoad = true;               /* boolean */

   this.gis_request_observer = null;
    
   /* Coefficient for canvas resolution. It will be adjusted in 
      SetCanvasResolution() for mobile devices with HiDPI displays as well as 
      on browser zoom.
   */
   this.CoordScale = 1;
}

////////////////////////////////////////////////////////////////////////////// 
// Script entry point: Starts trajectory demo by loading its drawing.
////////////////////////////////////////////////////////////////////////////// 
GlgTrajectory.prototype.Start = function()
{
   Debug( "Starting: " + this.GLG_div_name );

   this.Active = true;
   
   // Set initial size of the drawing.
   this.SetDrawingSize( false );

   /* Load a drawing from the specified drawing file. 
      The LoadCB callback will be invoked when the drawing has been loaded.

      Using "bind( this )" as a shorter way to provide "this" compared with 
      using lambda: with bind, we do not need to specify parameter list that
      we would need to provide for lambda.
   */
   let drawing_file = ( this.IsMobile ? "trajectory2.g" : "trajectory.g" );
   
   GLG.LoadWidgetFromURL( this.GetFullName( drawing_file ), null,
                          this.LoadCB.bind( this ), /*user data*/ null,
                          /*abort test function*/ ()=>!this.Active );
}

////////////////////////////////////////////////////////////////////////////// 
GlgTrajectory.prototype.GetFullName = function( drawing_name )
{
   if( this.GlgPath == null )
     return drawing_name;

   return this.GlgPath + "/" + drawing_name;
}

////////////////////////////////////////////////////////////////////////////// 
// Script entry point: Performs cleanup.
////////////////////////////////////////////////////////////////////////////// 
GlgTrajectory.prototype.Cleanup = function()
{
   Debug( "Cleanup for: " + this.GLG_div_name );
   
   this.Active = false;    // Ignore any pending updates and callbacks.

   if( this.Drawing )
     this.Drawing.ResetHierarchy();   // Undisplay GLG drawing.

   if( this.ResizeListener )
     window.removeEventListener( "resize", this.ResizeListener );
}

//////////////////////////////////////////////////////////////////////////////
GlgTrajectory.prototype.LoadCB = function( drawing, data, path )
{
   Debug( "LoadCB for: " + this.GLG_div_name );

   if( !this.Active )
     return;

   if( drawing == null )
   {
      AppAlert( "Can't load drawing, check console message for details." );
      return;
   }

   if( !document.getElementById( this.GLG_div_name ) )
   {
      Debug( "Can't find " + this.GLG_div_name +
             " div: it may have been removed from the document." );
      return;
   }
    
   // Disable spinning loader.   
   RemoveElement( this.GLG_div_name, "loader_container" );
    
   // Define the element in the HTML page to display the drawing.
   drawing.SetParentElement( this.GLG_div_name );
    
   // Disable viewport border to use the border of the glg_area.
   if( this.Standalone )
     drawing.SetDResource( "LineWidth", 0 );
    
   this.StartTrajectoryDemo( drawing );
}

//////////////////////////////////////////////////////////////////////////////
GlgTrajectory.prototype.StartTrajectoryDemo = function ( drawing )
{
   this.Drawing = drawing;
   
   this.InitDrawing();
   
   this.Drawing.SetupHierarchy();

   this.Drawing.Update();

   // Start animation.
   setTimeout( ()=>this.UpdatePosition(), UPDATE_INTERVAL );
}

//////////////////////////////////////////////////////////////////////////////
// Initializes satellite icons and orbits.
//////////////////////////////////////////////////////////////////////////////
GlgTrajectory.prototype.InitDrawing = function()
{
   // Get ID of the map viewport.
   this.Map = this.Drawing.GetResourceObject( "Map" );

   // Get ID of the GIS object.
   this.GISObject = this.Map.GetResourceObject( "GISObject" );   

   if( SuppliedMapServerURL != null )
     // Override the URL defined in the drawing.
     this.GISObject.SetSResource( "GISMapServerURL", SuppliedMapServerURL );
    
   /* Get ID of the GIS Object's GISArray, which holds all icons displayed
      on top of the map in lat/lon coordinates.
   */
   this.GISArray = this.GISObject.GetResourceObject( "GISArray" );

   /* Get trajectory templates and remove them from the drawing initially.
      Each template is a polygon with 2 points that stores graphical
      attributes of the trajectory, to allow the user to define 
      the attributes interactively via the Graphics Builder.
      One polygon is used for the edge of the trajectory, and another
      polygon is used for semi-transparent fill.
      Alternatively, template polygons can be created programmatically.
   */
   this.TrajectoryEdgeTemplate =
     this.Map.GetResourceObject( "TrajectoryEdgeTemplate" );
   this.Map.DeleteThisObject( this.TrajectoryEdgeTemplate );
      
   this.TrajectoryFillTemplate =
     this.Map.GetResourceObject( "TrajectoryFillTemplate" );
   this.Map.DeleteThisObject( this.TrajectoryFillTemplate );

   /* Vertical drop lines. */
   this.DropLineTemplate = this.Map.GetResourceObject( "DropLineTemplate" );
   this.Map.DeleteThisObject( this.DropLineTemplate );

   /* Delete craft icons from the top drawing, where it was placed for 
      the ease of editing. The selected icon will be added inside the 
      GISArray to position it in lat/lon. 
   */
   for( let i=0; i<2; ++i )
   {
      this.CraftIcon[i] =
        this.Map.GetResourceObject( i != 0 ? "Craft1" : "Craft0" );
      this.Map.DeleteThisObject( this.CraftIcon[ i ] );
   }
   
   /* Query the center of the GIS projection. */
   this.GISCenter = this.GISObject.GetGResource( "GISCenter" );

   // Set initial trajectory height and curvature to predefined values.
   this.Drawing.SetDResource( "Toolbar/HeightSlider/ValueX",
                              this.TrajectoryHeight );
   this.Drawing.SetDResource( "Toolbar/CurvatureSlider/ValueX",
                              this.Curvature );
   
   /* Set the GIS Zoom mode. It was set and saved with the drawing, 
      but do it again programmatically just in case.
   */
   this.Drawing.SetZoomMode( null, this.GISObject, null,
                             GLG.GlgZoomMode.GIS_ZOOM_MODE );
   
   this.SetZoomLevel();

   /* Add Input and Trace callbacks used to handle user interaction. */
   this.Drawing.AddListener( GLG.GlgCallbackType.INPUT_CB,
                             this.InputCallback.bind( this ) );
   this.Drawing.AddListener( GLG.GlgCallbackType.TRACE_CB,
                             this.TraceCallback.bind( this ) );

   if( USE_REQUEST )
     this.gis_request_observer =
       GLG.CreateGlgGISRequestObserver( this.RequestUpdate.bind( this ),
                                        this.RequestAdjustment.bind( this ),
                                        this.AdjustRequest.bind( this ) );
   this.SetMapLoading( true );
}

//////////////////////////////////////////////////////////////////////////
// Handle user interaction.
//////////////////////////////////////////////////////////////////////////
GlgTrajectory.prototype.InputCallback =
  function( /* GlgObject */ vp, /* GlgObject */ message_obj )
{
   if( !this.Active )
     return;

   let origin = message_obj.GetSResource( "Origin" );
   let format = message_obj.GetSResource( "Format" );
   let action = message_obj.GetSResource( "Action" );

   if( format == "Button" )         // Handle button clicks
   {
      if( action != "Activate" && action != "ValueChanged" )
        return;

      this.AbortNewTrajectoryMode();	 

      if( origin == "Restart" )
      {
         this.Restart();
      }
      else if( origin == "PauseResume" )
      {
         this.Pause = ( message_obj.GetDResource( "OnState" ) == 0 );
      }
      else if( origin == "IconType" )
      {
         this.CreateCraftIcon();   /* Change icon. */
         
         // Set craft position and angles, same as in UpdatePosition().
         if( this.Pause && this.UpdateCount != 0 ) 
         {
            this.GetCraftPosition( this.Position ); 
            this.Craft.SetGResourceFromPoint( "Position", this.Position ); 
            this.SetCraftAngles( this.Position, this.PrevPosition ); 
            this.Craft.SetDResource( "Visibility", 1.0 ); 
            this.Drawing.Update();
         }
      }
      else if( origin == "NewTrajectory" )
      {
         this.NewTrajectoryMode = true;
         this.Map.SetDResource( "Prompt/Visibility", 1.0 );
         this.Map.Update();
      }
      else if( origin == "Up" )
        this.Pan( 'u' );
      else if( origin == "Down" )
        this.Pan( 'd' );
      else if( origin == "Left" )
        this.Pan( 'l' );
      else if( origin == "Right" )
        this.Pan( 'r' );
      else if( origin == "ZoomIn" )
      {
         if( this.ZoomLevel < 4 )
         {
            ++this.ZoomLevel;
            this.SetZoomLevel();
         }
      }
      else if( origin == "ZoomOut" )
      {
         if( this.ZoomLevel > 0 )
         {
            --this.ZoomLevel;
            this.SetZoomLevel();
         }
      }
   }
   else if( format == "Slider" )
   {
      if( action == "ValueChanged" )
        this.AbortNewTrajectoryMode();
   }
}

//////////////////////////////////////////////////////////////////////////
// Restart simulation with new parameters.
//////////////////////////////////////////////////////////////////////////
GlgTrajectory.prototype.Restart = function()
{
   this.UpdateCount = 0;   // Reset update counter
   
   // Resume if was paused.
   this.Pause = false;
   this.Drawing.SetDResource( "Toolbar/PauseResume/OnState", 1.0 );
   this.Drawing.Update();
}
   
//////////////////////////////////////////////////////////////////////////
// Pan the map in the specified direction.
//////////////////////////////////////////////////////////////////////////
GlgTrajectory.prototype.Pan = function( /* String */ direction )
{
   let distance;    /* double */

   /* Pan in lat/lon instead of screen pixels to allow contineous rotation
      of the globe upside down.
   */
   switch( direction )
   {
    default:
    case 'u': direction = 'Y'; distance =  8.0; break;
    case 'd': direction = 'Y'; distance = -8.0; break;
    case 'l': direction = 'X'; distance = -8.0; break;
    case 'r': direction = 'X'; distance =  8.0; break;
   }

   this.SetMapLoading( true );

   if( USE_REQUEST )
   {
      this.Map.RequestGISZoom( null, direction, distance,
                               this.gis_request_observer );

      /* The new center of the GIS projection will be queried when the new map
         is installed. */
   }
   else
   {
      this.Map.SetZoom( null, direction, distance );
      this.Map.Update();
      
      /* Query the new center of the GIS projection. */
      this.GISCenter = this.GISObject.GetGResource( "GISCenter" );
   }
}

//////////////////////////////////////////////////////////////////////////
GlgTrajectory.prototype.SetZoomLevel = function()
{
   let scale;   /* double */
      
   switch( this.ZoomLevel )
   {
    case 0: scale = 3.0; break;
    case 1: scale = 5.0; break;
    default:
    case 2: scale = 8.25; break;
    case 3: scale = 10.0; break;
    case 4: scale = 12.0; break;
   }

   this.SetMapLoading( true );
   this.InitialLoad = true;
   
   /* Instead of the GIS zoom, GIS object is scaled (via the attached scale 
      transformation) to have the GIS object partially clipped out with 
      the top of it visible as a horizon line.      
      Scaling the GIS object does not change projection center.
   */
   this.GISObject.SetDResource( "GISScale", scale );
      
   // Set scaling limits depending on the resolution of available data.
   this.Map.SetDResource( "ZoomIn/DisableInput",
                          this.ZoomLevel >= 4 ? 10.0 : 0.0 );
   this.Map.SetDResource( "ZoomOut/DisableInput",
                          this.ZoomLevel <= 0 ? 1.0 : 0.0 );

   this.Map.Update();
}

//////////////////////////////////////////////////////////////////////////
// Updates moving object with new position data.
//////////////////////////////////////////////////////////////////////////
GlgTrajectory.prototype.UpdatePosition = function()
{   
   let
     trajectory,   /* GlgObject */
     drop_line;    /* GlgObject */

   /* If UseRequest=true, the map loading message will be erased when the 
      request is installed.
   */
   if( ( this.InitialLoad || ( !USE_REQUEST && !this.MapLoaded ) ) &&
       Math.trunc( this.GISObject.GetDResource( "ImageLoaded" ) ) != 0 )
   {
      this.SetMapLoading( false );
      this.InitialLoad = false;
   }

   if( !this.Pause )
   {
      if( this.UpdateCount == NUM_ITERATIONS )
        this.UpdateCount = 0; // Reached the end of the trajectory: start over.
         
      if( this.UpdateCount == 0 )     // First time - initialize.
      {
         this.InitSimulationParameters();
         this.CreateCraftIcon();
         this.GetCraftPosition( this.Position );
      }
      else
      {
         // Obtain new craft position. 
         this.GetCraftPosition( this.Position );
           
         /* Update craft position in the drawing with new data.
            Also update angles: previous position is valid.
         */
         this.Craft.SetGResourceFromPoint( "Position", this.Position );      
         this.SetCraftAngles( this.Position, this.PrevPosition );
         
         this.Craft.SetDResource( "Visibility", 1.0 );  // Make it visible.
      }

      // Store previous position to calculate heading angles.
      this.PrevPosition.CopyFrom( this.Position );

      // Update craft trajectory.
      if( this.UpdateCount == 0 )
      {
         // First time: add trajectory polygons to the GIS Object. 
         if( this.TrajectoryEdge != null )
         {
            // Destroy previous trajectory polygons.
            this.GISArray.DeleteThisObject( this.TrajectoryEdge );
            this.TrajectoryEdge = null;
            
            this.GISArray.DeleteThisObject( this.TrajectoryFill );
            this.TrajectoryFill = null;
            
            if( this.DropLineArray != null )
            {
               this.GISArray.DeleteThisObject( this.DropLineArray );
               this.DropLineArray = null;
            }
         }
         
         for( let i=0; i<2; ++i )
         {
            if( i == 0 )
            {
               this.TrajectoryEdge = this.TrajectoryEdgeTemplate.CopyObject();
               trajectory = this.TrajectoryEdge;
            }
            else
            {
               this.TrajectoryFill = this.TrajectoryFillTemplate.CopyObject();
               trajectory = this.TrajectoryFill;
            }
            
            // Use two points initially, set both points to the same lat/lon.
            SetPolygonPoint( trajectory, 0, this.Position );
            SetPolygonPoint( trajectory, 1, this.Position );
            
            /* Add to top to draw the trajectory first, with the craft 
               on top of it. */
            this.GISArray.AddObjectToTop( trajectory );
         }
      }
      else
      {
         this.GroundPosition.CopyFrom( this.Position );
         this.GroundPosition.z = 0.0;   // Zero elevation
         
         for( let i=0; i<2; ++i )
         {
            trajectory = ( i==0 ? this.TrajectoryEdge : this.TrajectoryFill );
               
            /* Add two points for every postion: one using the trajectory 
               elevation, and one at the ground level to show the ground 
               position.
            */
            AddPolygonPoint( trajectory, this.Position, true );
            AddPolygonPoint( trajectory, this.GroundPosition, false );
         }
         
         // Display a drop line for every DROP_LINE_INTERVAL iterations.
         if( ( this.UpdateCount % DROP_LINE_INTERVAL ) == 0 )
         {
            if( this.DropLineArray == null )
            {
               let position;   /* int */
               
               // First drop line: create a group to hold droplines.
               this.DropLineArray =
                 GLG.CreateObject( GLG.GlgObjectType.ARRAY,
                                   GLG.GlgContainerType.GLG_OBJECT,
                                   0, 0, null );
               
               // Add after trajectory polygons but before the craft.
               position = this.GISArray.GetIndex( this.Craft );
               this.GISArray.AddObjectAt( this.DropLineArray, position );
            }
            
            drop_line = this.DropLineTemplate.CopyObject();
            
            /* Draw a line from craft to the ground point (elev=0) with the
               same lat/lon.
            */
            SetPolygonPoint( drop_line, 0, this.Position );
            SetPolygonPoint( drop_line, 1, this.GroundPosition );
            
            this.DropLineArray.AddObjectToTop( drop_line );
         }
      }
      
      ++this.UpdateCount;
      this.Drawing.Update();
   }
   
   // Restart update timer
   setTimeout( ()=>this.UpdatePosition(), UPDATE_INTERVAL );
}

//////////////////////////////////////////////////////////////////////////
GlgTrajectory.prototype.SetMapLoading = function( /* boolean */ show )
{
   if( show )
   {
      this.Drawing.SetDResource( "Map/LoadingMessage/Visibility", 1 );
      this.MapLoaded = false;
   }
   else
   {
      this.Drawing.SetDResource( "Map/LoadingMessage/Visibility", 0 );
      this.MapLoaded = true;
   }
}

//////////////////////////////////////////////////////////////////////////
// Creates a craft icon based on the icon type requested by the IconType 
// toolbar button.
//////////////////////////////////////////////////////////////////////////
GlgTrajectory.prototype.CreateCraftIcon = function()
{
   let icon_type;   /* int */
      
   // Delete previously used craft icon.
   if( this.Craft != null )
   {
      this.GISArray.DeleteThisObject( this.Craft );
      this.Craft = null;
   }
   
   // Query icon type requested by the IconType toolbar button.
   icon_type =
     ( this.Drawing.GetDResource( "Toolbar/IconType/OnState" ) == 0 ? 0 : 1 );

   // Add a craft icon.
   this.Craft = this.CraftIcon[ icon_type ];
   
   if( this.IsMobile )
     // Increase icon size for mobile devices.
     this.Craft.SetDResource( "Scale", 2. );

   // Make invisible initially: has no position or angle information.
   this.Craft.SetDResource( "Visibility", 0.0 );
   
   // Add to the drawing inside the GIS object to position in lat/lon.
   this.GISArray.AddObjectToBottom( this.CraftIcon[ icon_type ] );
}

//////////////////////////////////////////////////////////////////////////
// Is used to obtain coordinates of the mouse click when defining 
// the trajectory's start and end points with the mouse.
//////////////////////////////////////////////////////////////////////////
GlgTrajectory.prototype.TraceCallback =
  function( /* GlgObject */ viewport, /* GlgTraceData */ trace_info )
{
   if( !this.Active )
     return;

   let event_type = trace_info.event_type;

   switch( event_type )
   {
    case GLG.GlgEventType.TOUCH_START:
    case GLG.GlgEventType.MOUSE_PRESSED:
      if( trace_info.button != 1 )
      {
         this.AbortNewTrajectoryMode();	 
         return;  // Use only the left button clicks.
      }
      break;
    default: break;
   }
   
   /* Use the Map area events only. */
   if( !trace_info.viewport.Equals( this.Map ) )
     return;
      
   switch( event_type )
   {
    case GLG.GlgEventType.TOUCH_START:
    case GLG.GlgEventType.MOUSE_PRESSED:
      if( !this.NewTrajectoryMode )
        return;

      if( event_type == GLG.GlgEventType.TOUCH_START )
        GLG.SetTouchMode();        /* Start dragging via touch events. */

      // Delete the previous trajectory polygon, if any.
      if( this.NewTrajectoryPolygon != null )
      {
         this.AbortNewTrajectoryMode();
         this.NewTrajectoryMode = true;    // Restore mode.
      }
      
      // Define the start point of the trajectory.
      this.CursorPosition.x = trace_info.mouse_x * this.CoordScale;
      this.CursorPosition.y = trace_info.mouse_y * this.CoordScale;
      this.CursorPosition.z = 0.0;
      
      /* COORD_MAPPING_ADJ is added to the cursor coordinates for precise 
         pixel mapping.
      */
      this.CursorPosition.x += GLG.COORD_MAPPING_ADJ;
      this.CursorPosition.y += GLG.COORD_MAPPING_ADJ;
      
      /* Convert screen coordinates of the mouse to the world coordinates 
         inside the GIS Object's GISArray - lat/lon. */ 
      this.GISArray.ScreenToWorld( true, this.CursorPosition, this.Position );
                     
      this.NewTrajectoryPolygon =
        GLG.CreateObject( GLG.GlgObjectType.POLYGON, 2, null, null, null );
      this.NewTrajectoryPolygon.SetGResource( "EdgeColor", 1.0, 1.0, 0.0 );
      
      // Initially, set both polygon points to the the mouse position.
      this.Point1.CopyFrom( this.Position );
      this.Point2.CopyFrom( this.Position );
      SetPolygonPoint( this.NewTrajectoryPolygon, 0, this.Point1 );
      SetPolygonPoint( this.NewTrajectoryPolygon, 1, this.Point1 );

      this.GISObject.AddObjectToBottom( this.NewTrajectoryPolygon );
      this.Map.Update();
      break;
      
    case GLG.GlgEventType.TOUCH_MOVED:
    case GLG.GlgEventType.MOUSE_MOVED:
      if( !this.NewTrajectoryMode || this.NewTrajectoryPolygon == null )
        return;

      // Start point has been defined: show the end point when dragging.
      this.CursorPosition.x = trace_info.mouse_x * this.CoordScale;
      this.CursorPosition.y = trace_info.mouse_y * this.CoordScale;
      this.CursorPosition.z = 0.0;
         
      /* COORD_MAPPING_ADJ is added to the cursor coordinates for 
         precise pixel mapping.
      */
      this.CursorPosition.x += GLG.COORD_MAPPING_ADJ;
      this.CursorPosition.y += GLG.COORD_MAPPING_ADJ;

      /* Convert screen coordinates of the mouse to the world coordinates 
         inside the GIS Object's GISArray - lat/lon.
      */ 
      this.GISArray.ScreenToWorld( true, this.CursorPosition, this.Position );
      this.Position.z = 0.0;   // Use elevation = 0
         
      // Store the end point of the trajectory.
      this.Point2.CopyFrom( this.Position );

      SetPolygonPoint( this.NewTrajectoryPolygon, 1, this.Position );
      this.Map.Update();
      break;

    case GLG.GlgEventType.TOUCH_END:
    case GLG.GlgEventType.MOUSE_RELEASED:
      if( trace_info.button != 1 ||
          !this.NewTrajectoryMode || this.NewTrajectoryPolygon == null )
        return;

      this.AbortNewTrajectoryMode();   // Delete NewTrajectoryPolygon.

      if( this.Point1.x == this.Point2.x && this.Point1.y == this.Point2.y )
        return;    // Mouse didn't move: no trajectory was defined.

      /* Trajectory was defined: store the new start and end point. */      
      this.StartPoint.CopyFrom( this.Point1 );
      this.EndPoint.CopyFrom( this.Point2 );

      // Set a smaller curvature to follow the start/end line.
      this.Curvature = -0.2;
      this.Drawing.SetDResource( "Toolbar/CurvatureSlider/ValueX",
                                 this.Curvature ); 
      
      this.Restart();             // Restart with the new trajectory.
      this.Drawing.Update();
      break;
      
    default: return;
   }
}

//////////////////////////////////////////////////////////////////////////
// Deletes NewTrajectoryPolygon if created.
//////////////////////////////////////////////////////////////////////////
GlgTrajectory.prototype.AbortNewTrajectoryMode = function()
{
   // Remove prompt if it's visible.
   this.Map.SetDResourceIf( "Prompt/Visibility", 0., true );

   if( this.NewTrajectoryPolygon != null )
   {
      if( this.GISObject.ContainsObject( this.NewTrajectoryPolygon ) )
        this.GISObject.DeleteThisObject( this.NewTrajectoryPolygon );
      
      this.NewTrajectoryPolygon = null;
      this.Map.Update();
   }
   this.NewTrajectoryMode = false;
}

//////////////////////////////////////////////////////////////////////////
// SIMULATION: returns craft postion (lon, lat and elevation above the
// Earth in meters) via the passed position parameter.
//////////////////////////////////////////////////////////////////////////
GlgTrajectory.prototype.GetCraftPosition = function( /* GlgPoint */ position )
{
   let rel_value, rel_sin;   /* double */
      
   rel_value = ( this.UpdateCount % NUM_ITERATIONS ) / NUM_ITERATIONS;
   rel_sin = Math.sin( Math.PI * rel_value );

   // Lon
   position.x =
     this.StartPoint.x + ( this.EndPoint.x - this.StartPoint.x ) * rel_value +
     this.NormalVector.x * this.TrajectoryLength * this.Curvature * rel_sin;

   // Lat
   position.y =
     this.StartPoint.y + ( this.EndPoint.y - this.StartPoint.y ) * rel_value +
     this.NormalVector.y * this.TrajectoryLength * this.CurvatureY * rel_sin;

   // Elevation
   position.z = this.TrajectoryHeight * rel_sin;
}

//////////////////////////////////////////////////////////////////////////
// SIMULATION: Set craft angles depending on its position on the globe
// and asimuthal angle. Angles are not needed if a simpler marker icon
// is used to mark position of the craft.
//////////////////////////////////////////////////////////////////////////
GlgTrajectory.prototype.SetCraftAngles =
  function( /* GlgPoint */ curr_position, /* GlgPoint */ prev_position )
{
   this.GetGlobeAngles( curr_position, prev_position, this.CraftAngles );
   
   let yaw = this.CraftAngles.x;     /* double */
   let pitch = this.CraftAngles.y;   /* double */
   let roll = this.CraftAngles.z;    /* double */
   
   this.Craft.SetDResource( "Roll", roll );
   this.Craft.SetDResource( "Pitch", pitch );
   this.Craft.SetDResource( "Yaw", yaw );
}

//////////////////////////////////////////////////////////////////////////
// SIMULATION: calculates craft's heading angles based on the current
// and previous position. The angles are returned via the passed angles
// parameter.
//////////////////////////////////////////////////////////////////////////
GlgTrajectory.prototype.GetGlobeAngles =
  function( /* GlgPoint */ curr_position, /* GlgPoint */ prev_position,
            /* GlgPoint */ angles )
{
   let            /* double */
     dx, dy, dz,
     rel_value,
     yaw, pitch, roll;
      
   /* Calculate angles using XYZ position on the globe. If Z coordinate
      is not required, GlgGISConvert() may be used to get X and Y screen 
      coordinates in pixels instead of GetPointXYZ().
   */
   
   /* XYZ of the first point, in meters */
   this.GetPointXYZ( curr_position, this.CurrXYZ );
   
   /* XYZ of the second point, in meters */
   this.GetPointXYZ( prev_position, this.PrevXYZ );
   
   dx = this.CurrXYZ.x - this.PrevXYZ.x;
   dy = this.CurrXYZ.y - this.PrevXYZ.y;
   dz = this.CurrXYZ.z - this.PrevXYZ.z;
   
   /* Visible heading angles on the globe in the current projection. */
   if( dx == 0.0 )
     yaw = ( dy != 0.0 ? ( dy > 0.0 ? 90.0 : -90.0 ) : 0.0 );
   else
   {
      yaw = Math.atan( dy / dx );
      yaw = RadToDeg( yaw );
      if( dx < 0.0 )
        yaw += 180.0;
   }
   
   if( dx == 0.0 && dy == 0.0 )
     pitch = - ( dz != 0.0 ? ( dz > 0.0 ? 90.0 : -90.0 ): 0.0 );
   else
   {
      pitch = - Math.atan( dz / Math.sqrt( dx * dx + dy * dy ) / 3.0 );
      pitch = RadToDeg( pitch );
   }
   
   rel_value = ( this.UpdateCount % NUM_ITERATIONS ) / NUM_ITERATIONS;
   
   // Roll it a bit. In an application, supply a real roll angle.
   roll = -60.0 * Math.sin( DegToRad( yaw ) ) * Math.sin( Math.PI * rel_value );

   angles.x = yaw;
   angles.y = pitch;
   angles.z = roll;
}

//////////////////////////////////////////////////////////////////////////
// SIMULATION: Initializes simulation parameters.
//////////////////////////////////////////////////////////////////////////
GlgTrajectory.prototype.InitSimulationParameters = function()
{
   let max_lat;   /* double */
      
   // Query requested trajectory height from the slider.
   this.TrajectoryHeight =
     this.Drawing.GetDResource( "Toolbar/HeightSlider/ValueX" );

   // Query requested trajectory curvature from the slider.
   this.Curvature =
     this.Drawing.GetDResource( "Toolbar/CurvatureSlider/ValueX" );

   this.TrajectoryVector.x = this.EndPoint.x - this.StartPoint.x;
   this.TrajectoryVector.y = this.EndPoint.y - this.StartPoint.y;
   
   // Obtain a normal vector used to curve the simulated trajectory.
   GetNormalVector( this.TrajectoryVector, this.NormalVector );
   
   /* Calculate trajectory length in abstract units for simulation. */
   this.TrajectoryLength =
     Math.sqrt( this.TrajectoryVector.x * this.TrajectoryVector.x +
                this.TrajectoryVector.y * this.TrajectoryVector.y );

   /* Point with the maximum lat. */
   max_lat = this.StartPoint.y + ( this.EndPoint.y - this.StartPoint.y ) * 
     this.NormalVector.y * this.TrajectoryLength * this.Curvature;
   if( max_lat > 89.0 )
     max_lat = 89.0;
   else if( max_lat < -89.0 )
     max_lat = -89.0;
   else 
     max_lat = 0.0;
   
   // Limit Y curvature to avoid flying "off the globe".
   if( max_lat != 0.0 )
     this.CurvatureY = ( max_lat - this.StartPoint.y ) / 
       ( this.NormalVector.y * this.TrajectoryLength *
         ( this.EndPoint.y - this.StartPoint.y ) );
   else
     this.CurvatureY = this.Curvature;
}

//////////////////////////////////////////////////////////////////////////
// SIMULATION: Returns 2D normal vector of length=1.
//////////////////////////////////////////////////////////////////////////
function GetNormalVector( /* GlgPoint */ vector, /* GlgPoint */ normal )
{
   let length;   /* double */
      
   if( vector.x == 0.0 )
   {
      if( vector.y == 0.0 )        
        normal.x = 0.0;    // NULL vector: return NULL as a normal.
      else
        normal.x = 1.0;
      
      normal.y = 0.0;
   }
   else if( vector.y == 0.0 )
   {
      normal.x = 0.0;
      normal.y = 1.0;
      }
   else
   {
      normal.x = vector.y;
      normal.y = - vector.x;
      length = Math.sqrt( normal.x * normal.x + normal.y * normal.y );
      if( length != 1.0 )
      {
            normal.x /= length;
            normal.y /= length;
      }
   }
}

//////////////////////////////////////////////////////////////////////////
// Returns XYZ coordinate of the lat/lon point, in meters.
//////////////////////////////////////////////////////////////////////////
GlgTrajectory.prototype.GetPointXYZ =
  function( /* GlgPoint */ lat_lon, /* GlgPoint */ xyz )
{
   let               /* double */
     globe_radius,
     phi, phi1, lon_diff,
     cos_phi, sin_phi,
     cos_phi1, sin_phi1,
     cos_lon_diff, sin_lon_diff;
   
   phi = DegToRad( lat_lon.y );
   phi1 = DegToRad( this.GISCenter.y );
   lon_diff = DegToRad( lat_lon.x - this.GISCenter.x );
   
   // Use the average value plus elevation.
   globe_radius = ( GLG.EQUATOR_RADIUS + GLG.POLAR_RADIUS ) / 2.0;
   globe_radius += lat_lon.z;
      
   cos_phi = Math.cos( phi );
   sin_phi = Math.sin( phi );
   cos_phi1 = Math.cos( phi1 );
   sin_phi1 = Math.sin( phi1 );
   cos_lon_diff = Math.cos( lon_diff );
   sin_lon_diff = Math.sin( lon_diff );
   
   xyz.x = globe_radius * cos_phi * sin_lon_diff;
   xyz.y = globe_radius *
     ( cos_phi1 * sin_phi - sin_phi1 * cos_phi * cos_lon_diff );
   xyz.z = globe_radius *
     ( cos_lon_diff * cos_phi1 * cos_phi + sin_phi1 * sin_phi );
}

//////////////////////////////////////////////////////////////////////////
function SetPolygonPoint( /* GlgObject */ polygon, /* int */ point_index,
                          /* GlgPoint */ position )
{
   let point;   /* GlgObject */

   point = polygon.GetElement( point_index );
   point.SetGResourceFromPoint( null, position );
}

//////////////////////////////////////////////////////////////////////////
function AddPolygonPoint( /* GlgObject */ polygon, /* GlgPoint */ position,
                          /* boolean */ add_at_the_end )
{
   let point;   /* GlgObject */

   // Copy the first point and add it at the end of the polygon.
   point = polygon.GetElement( 0 );
   point = point.CopyObject();

   point.SetGResourceFromPoint( null, position );
   if( add_at_the_end )
     polygon.AddObjectToBottom( point );
   else                // Add at the beginning of the polygon.
     polygon.AddObjectToTop( point );
}

//////////////////////////////////////////////////////////////////////////
function DegToRad( /* double */ angle )   /* double */
{
   return angle / 180.0 * Math.PI;
}

//////////////////////////////////////////////////////////////////////////
function RadToDeg( /* double */ angle )   /* double */
{
   return angle / Math.PI * 180.0;
}

//////////////////////////////////////////////////////////////////////////////
// Changes drawing size while maintaining width/height aspect ratio.
//////////////////////////////////////////////////////////////////////////////
GlgTrajectory.prototype.SetDrawingSize = function( next_size )
{
   const ASPECT_RATIO = 450 / 525;
   
   const MIN_WIDTH = 500;
   const MAX_WIDTH = 1000;
   const SCROLLBAR_WIDTH = 15;

   let span = document.body.clientWidth - SCROLLBAR_WIDTH;    

   if( this.SizeIndex == undefined )   // First time: initialize.
   {
      this.SizeIndex = 0;

      if( this.IsMobile )
      {
         /* Mobile devices use fixed device-width: disable Change Drawing Size 
            button. If it's not standalone, it is handled outside of this 
            script.
         */
         if( this.Standalone )
           RemoveElement( null, this.GLG_div_name + "_change_size" );
      }
      else   /* Desktop */
      {      
         const small_sizes  = [ 1, 1.5,  2.,   2.5 ];
         const medium_sizes = [ 1, 0.75, 1.25, 1.5 ];
         const large_sizes  = [ 1, 0.6,  1.25, 1.5 ];
         
         if( span < 600 )
           this.SetDrawingSize.size_array = small_sizes;
         else if( span < 800 )
           this.SetDrawingSize.size_array = medium_sizes;
         else
           this.SetDrawingSize.size_array = large_sizes;
         
         this.SetDrawingSize.num_sizes = this.SetDrawingSize.size_array.length;
         
         /* Handle browser zooming. */
         this.ResizeListener = ()=>this.SetDrawingSize( false );
         window.addEventListener( "resize", this.ResizeListener );
      }
   }
   else if( next_size )
   {
      ++this.SizeIndex;
      this.SizeIndex %= this.SetDrawingSize.num_sizes;
   }
    
   let drawing_area = document.getElementById( this.GLG_div_name );
   if( this.IsMobile )
   {
      /* Mobile devices use constant device-width, adjust only the height 
         of the drawing to keep the aspect ratio.
      */
      drawing_area.style.height =
        "" + Math.trunc( drawing_area.clientWidth / ASPECT_RATIO ) + "px";
   }
   else   /* Desktop */
   {
      let start_width;
      if( span < MIN_WIDTH )
        start_width = MIN_WIDTH;
      else if( span > MAX_WIDTH )
        start_width = MAX_WIDTH;
      else
        start_width = span;

      let size_coeff = this.SetDrawingSize.size_array[ this.SizeIndex ];
      let width = Math.trunc( Math.max( start_width * size_coeff, MIN_WIDTH ) );
      drawing_area.style.width = "" + width + "px";
      drawing_area.style.height = 
        "" + Math.trunc( width / ASPECT_RATIO ) + "px";
   }

   // Adjust canvas resolution for mobile devices and browser zoom state.
   if( !next_size )
     this.SetCanvasResolution();
}

//////////////////////////////////////////////////////////////////////////////
// Increases canvas resolution for mobile devices with HiDPI displays and for
// browser zooming. Sets CoordScale global variable.
//////////////////////////////////////////////////////////////////////////////
GlgTrajectory.prototype.SetCanvasResolution = function()
{
   let TextScale, PixelOffsetScale, ScreenCoordScale, NativeWidgetTextScale;

   if( this.IsMobile )
   {
      /* CoordScale parameter defines canvas coordinate scaling.
         Values greater than 1 increase canvas resolution and result in 
         sharper rendering. On mobile devices with devicePixelRatio > 1,
         the value of devicePixelRatio may be used for very crisp rendering
         with very thin lines. For pixel ration greater than 2, limit 
         CoordScale to 2 to draw thicker lines.

         CanvasScale > 1 makes text smaller. The TextScale parameter defines
         the text scaling factor used to increase text size.

         The ScreenCoordScale parameter specifies a scaling factor for fixed
         scale viewports that use screen coordinates. If the size of the fixed
         scale viewport (such as a height of a toolbar) is controlled by a pixel
         offset transformation, ScreenCoordScale may be set to the same value as
         PixelOffsetScale to scale the content of the viewport proportionally
         to its height increase.

         The NativeWidgetTextScale parameter defines the scaling factor that is
         used to scale down text in native widgets (such as native buttons, 
         toggles, etc.) to match the scale of the drawing.
      */
      if( window.devicePixelRatio > 2. )
        this.CoordScale = 2.;
      else
        this.CoordScale = window.devicePixelRatio;

      if( this.IsMobile )
        TextScale = 1.75;
      else
        TextScale = 1.2;
      
      TextScale = 1.75;
      PixelOffsetScale = TextScale;
      ScreenCoordScale = PixelOffsetScale;
      NativeWidgetTextScale = 0.6;
   }
   else   // Desktop
   {
      /* Change canvas resolution to match browser zoom state. */
      this.CoordScale = window.devicePixelRatio;      
      TextScale = window.devicePixelRatio;
      PixelOffsetScale = window.devicePixelRatio;
      ScreenCoordScale = window.devicePixelRatio;
      NativeWidgetTextScale = 1.;    // Don't change native text size.
   }

   GLG.SetCanvasScale( this.CoordScale, TextScale, NativeWidgetTextScale,
                       PixelOffsetScale, ScreenCoordScale );
}

//////////////////////////////////////////////////////////////////////////
function RemoveElement( parent_id, child_id )
{
   let element;
   if( parent_id == null )
     // No parent was provided: remove from document.
     element = document.getElementById( child_id );
   else
   {
      // Parent ID was provided: remove from parent.
      let parent = document.getElementById( parent_id );
      if( parent == null )
        return;
      element = parent.querySelector( "#" + child_id );
   }
   
   if( element != null )
     element.parentNode.removeChild( element );
}

//////////////////////////////////////////////////////////////////////////////
function Debug( message )
{
   if( DEBUG )
     console.log( message );
}

//////////////////////////////////////////////////////////////////////////////
function AppAlert( message )
{
   window.alert( message );
}

//////////////////////////////////////////////////////////////////////////////
function AppLog( message )
{
   console.log( message );
}

//////////////////////////////////////////////////////////////////////////
// GIS request observer interface, requests no adjustement callbacks.
//////////////////////////////////////////////////////////////////////////
GlgTrajectory.prototype.RequestAdjustment = function()
{
   return false;
}
//////////////////////////////////////////////////////////////////////////
// GIS request observer interface: adjustement callback.
//////////////////////////////////////////////////////////////////////////
GlgTrajectory.prototype.AdjustRequest = function( /* GlgGISRequestData */ request_data )
{
   return false;
}

//////////////////////////////////////////////////////////////////////////
// GIS request observer interface: receives status update notifications.
//////////////////////////////////////////////////////////////////////////
GlgTrajectory.prototype.RequestUpdate = function( /* GlgObject */ gis_object, status )
{
   switch( status )
   {
    case GLG.GlgGISRequestStatus.GIS_REQUEST_READY:
      if( this.GISObject.InstallGISRequest( null ) )
      {
         this.Map.Update();

         /* Query the new center of the GIS projection. */
         this.GISCenter = this.GISObject.GetGResource( "GISCenter" );
      }
      else
        AppLog( "Failed to install map request." );
      break;
      
    default:
      AppLog( "Aborting previous GIS request." ); 
      break;
   }

   this.SetMapLoading( false );
}
